import { useEffect, useRef, useState } from "react";

// Pinch-zoom + pan + double-tap image viewer.
// - Two-finger pinch scales between 1× and 4×
// - When zoomed, single-finger drag pans
// - Double-tap toggles between 1× and 2.5×
// - Mouse wheel zooms on desktop
// - Zoom resets when `resetKey` changes
export default function ZoomableImage({ src, alt = "", resetKey }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const wrapRef = useRef(null);

  // Pointer / touch tracking
  const pointers = useRef(new Map());
  const gesture = useRef(null); // {startDist, startScale, startTx, startTy, startMid}
  const lastTap = useRef(0);

  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [resetKey, src]);

  const clampPan = (nextScale, nx, ny) => {
    const el = wrapRef.current;
    if (!el) return { x: nx, y: ny };
    const w = el.clientWidth;
    const h = el.clientHeight;
    const overflowX = Math.max(0, (w * nextScale - w) / 2);
    const overflowY = Math.max(0, (h * nextScale - h) / 2);
    return {
      x: Math.min(overflowX, Math.max(-overflowX, nx)),
      y: Math.min(overflowY, Math.max(-overflowY, ny)),
    };
  };

  const setScaleClamped = (s) => {
    const ns = Math.max(1, Math.min(4, s));
    setScale(ns);
    // recentre if fully zoomed out
    if (ns === 1) { setTx(0); setTy(0); }
    return ns;
  };

  const onPointerDown = (e) => {
    if (!wrapRef.current) return;
    wrapRef.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      gesture.current = {
        mode: "pinch",
        startDist: dist,
        startScale: scale,
      };
    } else if (pointers.current.size === 1) {
      gesture.current = {
        mode: "pan",
        startX: e.clientX,
        startY: e.clientY,
        startTx: tx,
        startTy: ty,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;

    if (g.mode === "pinch" && pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const ratio = dist / g.startDist;
      const next = setScaleClamped(g.startScale * ratio);
      const clamped = clampPan(next, tx, ty);
      setTx(clamped.x);
      setTy(clamped.y);
    } else if (g.mode === "pan" && scale > 1) {
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      const clamped = clampPan(scale, g.startTx + dx, g.startTy + dy);
      setTx(clamped.x);
      setTy(clamped.y);
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      // double-tap detect
      const now = Date.now();
      if (now - lastTap.current < 300) {
        const next = scale > 1 ? 1 : 2.5;
        setScaleClamped(next);
      }
      lastTap.current = now;
      gesture.current = null;
    } else if (pointers.current.size === 1) {
      const remaining = [...pointers.current.entries()][0];
      gesture.current = {
        mode: "pan",
        startX: remaining[1].x,
        startY: remaining[1].y,
        startTx: tx,
        startTy: ty,
      };
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    const next = setScaleClamped(scale + dir * 0.25);
    if (next === 1) { setTx(0); setTy(0); }
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full flex items-center justify-center overflow-hidden select-none"
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      data-testid="lightbox-zoom-wrap"
      data-scale={scale.toFixed(2)}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="max-w-[92vw] max-h-[86vh] object-contain pointer-events-none"
        style={{
          transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
          transformOrigin: "center center",
          transition: gesture.current ? "none" : "transform 200ms ease-out",
          willChange: "transform",
        }}
        data-testid="lightbox-image"
      />
      {scale > 1 && (
        <div
          className="absolute bottom-4 right-4 px-3 py-1 font-micro text-[#EDE4D3] bg-[#0B0B0C]/70"
          style={{ border: "1px solid var(--hairline)" }}
          data-testid="lightbox-zoom-badge"
        >
          {Math.round(scale * 100)}%
        </div>
      )}
    </div>
  );
}
